import { AuthClient, decodeToken, authCall } from '@skytech/auth'
import type { AuthCall } from '@skytech/auth'
import type { AuthError } from '@skytech/auth'
import { config } from '@skytech/config'

import { API_PATH_STATUS } from '@/api/apiRoutes'
import queries from '@/api/queries'
import { queryClient } from '@/queryClient'
import type { Status, TokenData } from '@/types'

import getUserFromToken from './getUserFromToken'

class RefreshTokenClient {
  private refreshTokenPromise: null | Promise<unknown> = null

  // To prevent multiple refresh-token-requests
  public async refreshToken() {
    if (this.refreshTokenPromise) return this.refreshTokenPromise

    this.refreshTokenPromise = refreshTokenData()

    return this.refreshTokenPromise.then(() => {
      this.refreshTokenPromise = null
    })
  }
}
const RefreshTokenRequestsClient = new RefreshTokenClient()

function getApiPath(path: string, isNewBackendApi = false) {
  return (isNewBackendApi ? config.APP_NEW_API_URL : config.API_URL) + path
}

const appAuthCall = async (args: AuthCall, isNewBackendApi = false) => {
  const path = getApiPath(args.path, isNewBackendApi)

  try {
    const results = await authCall({
      ...args,
      path,
    })

    return results
  } catch (error) {
    const err: AuthError = error
    if (err.response.status === 401) {
      await RefreshTokenRequestsClient.refreshToken()
      return authCall({
        ...args,
        path,
      })
    }

    throw err
  }
}

const refreshTokenData = async () => {
  // Ask for a new token based on the refresh token
  const tokenResponse: Response = await fetch(config.AUTH_API_URL + 'app/token', {
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })

  if (!tokenResponse.ok) {
    AuthClient.logOut({ isTriggeredProgrammatically: true })
    return
  }

  const { token }: { token: string } = await tokenResponse.json()
  const tokenData = decodeToken<TokenData>(token)

  const currentToken = AuthClient.getToken()

  const currentTokenData = currentToken ? decodeToken<TokenData>(currentToken) : null
  // In case the refreshed token belongs to a different user (multiple open tabs case)
  if (currentTokenData?.coid !== tokenData.coid) {
    const statusResponse: Response = await fetch(config.API_URL + API_PATH_STATUS, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    })

    if (!statusResponse.ok) {
      AuthClient.logOut({ isTriggeredProgrammatically: true })
      return
    }

    const status: Status = await statusResponse.json()
    const queryCache = queryClient.getQueryCache()
    const queriesToOmit = [queries.user(), queries.status(), queries.translationList()]
    queryCache.getAll().forEach(({ queryKey }) => {
      if (queriesToOmit.includes(queryKey as string[])) return
      queryClient.removeQueries(queryKey)
    })
    const updatedUser = getUserFromToken(tokenData)
    queryClient.setQueryData(queries.user(), updatedUser)
    queryClient.setQueryData(queries.status(), status)
  }

  AuthClient.setToken(token)
}

export const get = async <T>(path: string, isNewBackendApi = false): Promise<T> =>
  appAuthCall({ path, method: 'GET' }, isNewBackendApi)

export async function post<R = any>(
  path: string,
  data?: unknown,
  isNewBackendApi = false
): Promise<R> {
  return appAuthCall(
    {
      path,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
    isNewBackendApi
  )
}

export async function del<R = any>(path: string, isNewBackendApi = false): Promise<R> {
  return appAuthCall(
    {
      path,
      method: 'DELETE',
    },
    isNewBackendApi
  )
}
